import { Box, VBox } from "./Box";
import { Icon } from "./Icon";
import { classNames } from "../utils/classNames";
import { BetaLabel } from "./BetaLabel";
import {
  ButtonHTMLAttributes,
  FocusEventHandler,
  forwardRef,
  Fragment,
  ReactElement,
  useCallback,
  useState,
} from "react";
import { DropdownMenu, DropdownMenuItemType } from "./Dropdown";
import { formatOrgNum } from "../utils/other";
import { Tag } from "./Tag";
import { Button, IconButton } from "./Button";
import { accessColors, accessLabels, AccessType } from "../types/enums/AccessType";
import { isEmpty, noop } from "lodash";
import { isMobileView } from "../utils/screen";
import { useFloatingDropdown } from "./floatingHelpers";
import { Popover } from "@headlessui/react";
import { FloatingOverlay } from "./FloatingOverlay";
import { Tabs } from "./Tabs";
import styles from "./AppBar.module.css";
import {
  AppBarData,
  AppBarLink,
  CompanyOption,
  getTotalSignDocumentCount,
  groupBySigningMethod,
} from "../appBar/appBarDomain";
import { Avatar, AvatarButton } from "./Avatar";
import { Logo } from "./Logo";
import { isTestEnvironment } from "../utils/environments";
import { TMessageKey } from "../i18n/types.gen";
import { PortfolioSelector } from "../portfolio/portfolioComponents";
import { signDocumentLabel, signingMethodLabel } from "../common/common";
import { TMessage } from "../i18n/tt";
import { useTranslation } from "../stores/RootStoreContext";
import { preventDefault } from "../utils/events";
import { SubscriptionType } from "../common/subscription";
import { SubLink } from "../appBar/headerLinks";
import { MenuBar } from "./MenuBar";

function SubMenuLink({
  url,
  text,
  badge,
  tag,
  isActive,
  onFocus,
  className,
  badgeColor,
}: {
  url: string;
  text: TMessage;
  badge?: SubLink["badge"];
  tag?: SubLink["tag"];
  isActive?: boolean;
  onFocus: FocusEventHandler<HTMLElement>;
  className: string;
  badgeColor?: SubLink["badgeColor"];
}) {
  const { t } = useTranslation();
  return (
    <a
      href={url}
      className={classNames(
        className,
        "tw-outline-offset-[-4px]",
        "tw-no-underline",
        "hover:bg-alt1",
        isActive
          ? "color-default visited:color-default hover:color-default"
          : "subtle visited:subtle hover:color-default",
      )}
      onFocus={onFocus}
    >
      <Box alignItems={"center"} gap={"sm"} whitespace={"nowrap"}>
        <span className={"link-text"}>{t(text)}</span>
        {tag && <Tag compact label={t(tag)} color={"error"} />}
        {badge && <BetaLabel label={badge} color={badgeColor} />}
      </Box>
    </a>
  );
}

function PortfolioSettings({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <a
      href={""}
      className={classNames(
        "tw-p-2",
        "tw-rounded-lg",
        "tw-no-underline",
        "color-default",
        "active:color-default",
        "visited:color-default",
        "hover:color-default",
        "hover:bg-alt1",
      )}
      data-tooltip={t("portfolio.my-investments")}
      onClick={preventDefault(onClick)}
    >
      <Box alignItems={"center"} gap={"sm"} whitespace={"nowrap"}>
        <Icon name="gear" color={"info"} />
        {t("portfolio.portfolio-settings")}
      </Box>
    </a>
  );
}

function HelpCenterLink({ url, icon, text, isActive }: AppBarLink) {
  const { t } = useTranslation();
  return (
    <a
      href={url}
      className={classNames(
        "tw-p-2",
        "tw-rounded-lg",
        "tw-no-underline",
        "color-default",
        "active:color-default",
        "visited:color-default",
        "hover:color-default",
        "hover:bg-alt1",
        isActive && "bg-alt2-dimmed",
      )}
      data-tooltip={t(text)}
    >
      <Box alignItems={"center"} gap={"sm"} whitespace={"nowrap"}>
        <Icon name={icon} color={"info"} />
      </Box>
    </a>
  );
}

function AccessTag({ accessType }: { accessType: AccessType }) {
  const { t } = useTranslation();
  return <Tag label={t(accessLabels[accessType])} color={accessColors[accessType]} />;
}

const CompanyName = ({ company }: { company: CompanyOption }) => {
  const { t } = useTranslation();
  return (
    <div className={"tw-min-w-0"}>
      <div
        className={"tw-font-semibold tw-overflow-hidden tw-overflow-ellipsis tw-whitespace-nowrap"}
      >
        {company.name}
      </div>
      <div className={"text-sm tw-overflow-hidden tw-overflow-ellipsis tw-whitespace-nowrap"}>
        {t("dashboard.orgNo")} {formatOrgNum(company.organisationNumber)}
      </div>
    </div>
  );
};

const CompanySelectorButton = forwardRef<
  HTMLButtonElement,
  {
    selectedCompany: Required<AppBarData>["selectedCompany"];
    isOpen: boolean;
  } & ButtonHTMLAttributes<HTMLButtonElement>
>(function CompanySelectorButton({ selectedCompany, isOpen, className, ...props }, ref) {
  return (
    <button
      {...props}
      ref={ref}
      className={classNames(
        className,
        "tw-outline-offset-[-4px]",
        "tw-flex",
        "tw-items-center",
        "tw-gap-2",
        "tw-min-w-0",
      )}
    >
      <CompanyName company={selectedCompany} />
      <Icon name={isOpen ? "caret-up" : "caret-down"} />
    </button>
  );
});

function CompanySelector({
  selectedCompany,
  companies,
  subscription,
  actions,
  maximizeDropdown,
  buttonClassName,
}: AppBarData & {
  maximizeDropdown?: boolean;
  buttonClassName?: string;
}) {
  const { t } = useTranslation();
  if (!selectedCompany) {
    return null;
  }
  if (!companies) {
    return <CompanyName company={selectedCompany} />;
  }
  return (
    <DropdownMenu
      maximize={maximizeDropdown}
      disableOffset={maximizeDropdown}
      button={({ ref, isOpen, ...props }) => (
        <CompanySelectorButton
          ref={(el) => {
            if (el && maximizeDropdown) {
              ref(el.closest("header"));
            } else {
              ref(el);
            }
          }}
          className={buttonClassName}
          isOpen={isOpen}
          selectedCompany={selectedCompany}
          {...props}
        />
      )}
      items={[
        {
          type: "custom-item",
          render: () => (
            <Box alignItems={"center"} padding>
              <Box padding bg={"alt1"} rounded={"full"}>
                <Icon name={"bank"} color={"info"} />
              </Box>
              <div>
                <div>{selectedCompany.name}</div>
                <div>
                  <small className={"subtle"}>
                    {formatOrgNum(selectedCompany.organisationNumber)}
                  </small>
                </div>
              </div>
              <AccessTag accessType={selectedCompany.accessType} />
            </Box>
          ),
          onSelect: actions.navigateToPortfolio,
        },
        !subscription &&
          actions.openPaymentDialog && {
            type: "custom-item",
            canSelect: false,
            render: ({ close }) => (
              <VBox padding gap={"md"}>
                <div>{t("companyMenu.freemium.description")}</div>
                <VBox>
                  <Button
                    variant={"primary"}
                    fullWidth
                    label={t("companyMenu.freemium.becomeCustomer")}
                    onClick={(_, dispatch) => {
                      if (actions.openPaymentDialog) {
                        close();
                        dispatch(actions.openPaymentDialog);
                      }
                    }}
                  />
                  <small className={"subtle"}>{t("companyMenu.freemium.subDescription")}</small>
                </VBox>
              </VBox>
            ),
          },
        subscription?.subscriptionType === SubscriptionType.BASIC &&
          actions.upgradeToPro && {
            type: "custom-item",
            canSelect: false,
            render: ({ close }) => (
              <VBox padding gap={"md"}>
                <div>{t("companyMenu.basic.description")}</div>
                <VBox>
                  <Button
                    variant={"primary"}
                    fullWidth
                    label={t("companyMenu.basic.upgradeToPro")}
                    onClick={() => {
                      close();
                      actions.upgradeToPro?.();
                    }}
                  />
                  <small className={"subtle"}>{t("companyMenu.basic.subDescription")}</small>
                </VBox>
              </VBox>
            ),
          },
        subscription?.subscriptionType !== SubscriptionType.PRO &&
          actions.navigateToPrices && {
            type: "item",
            label: (
              <Box>
                <div>{t("companyMenu.freemium.link-to-all-functions")}</div>
                <Icon name={"arrow-square-out"} />
              </Box>
            ),
            onSelect: actions.navigateToPrices,
          },
        selectedCompany.accessType === AccessType.ADMIN &&
          actions.openCompanySettings && {
            type: "item",
            label: t("companyMenu.links.companySettings"),
            onSelect: actions.openCompanySettings,
          },
        selectedCompany.accessType === AccessType.ADMIN &&
          actions.addAdministrator && {
            type: "item",
            label: t("companyMenu.links.addAdministrator"),
            onSelect: () =>
              actions.addAdministrator?.({
                organisationNumber: selectedCompany.organisationNumber,
                companyName: selectedCompany?.name ?? "",
              }),
          },
        selectedCompany.accessType === AccessType.ADMIN &&
          actions.navigateToContactUs && {
            type: "item",
            label: (
              <Box>
                <div>{t("companyMenu.freemium.link-to-contact-sales")}</div>
                <Icon name={"arrow-square-out"} />
              </Box>
            ),
            onSelect: actions.navigateToContactUs,
          },
        companies && { type: "separator" },
        companies && { type: "label", label: t("companyMenu.changeToSubheader") },
        ...(companies ?? []).map<DropdownMenuItemType>((c) => ({
          type: "item",
          label: (
            <Box alignItems={"center"} justifyContent={"space-between"} grow>
              <div>{c.name}</div>
              <AccessTag accessType={c.accessType} />
            </Box>
          ),
          onSelect: () => actions.selectCompany?.(c),
        })),
      ]}
    />
  );
}

const accessLabelMappings: { [key in AccessType]: TMessageKey } = {
  [AccessType.ADMIN]: "accessType.ADMIN",
  [AccessType.SHAREHOLDER]: "accessType.SHAREHOLDER",
  [AccessType.ADDITIONAL_REPRESENTATIVE]: "accessType.ADDITIONAL_REPRESENTATIVE",
  [AccessType.BOARD_MEMBER]: "accessType.BOARD_MEMBER",
  [AccessType.SUPERADMIN]: "accessType.SUPERADMIN",
  [AccessType.NONE]: "accessType.NONE",
  [AccessType.DRAFT]: "accessType.DRAFT",
  [AccessType.CUSTOM]: "custom",
};

export function UserMenu({
  languages,
  user,
  selectedCompany,
  actions,
  className,
  userMainAction,
  disableUserContextInMenu,
}: AppBarData & {
  className?: string;
  disableUserContextInMenu?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <DropdownMenu
      alignment={"end"}
      button={({ ref }) => (
        <AvatarButton ref={ref} className={className} textSize={"normal"} color={"primary"}>
          {user.initials}
        </AvatarButton>
      )}
      items={[
        !disableUserContextInMenu && {
          type: "custom-item",
          render: () => (
            <VBox padding gap={"md"}>
              <Box className={"tw-min-w-[18rem]"} gap={"md"}>
                <Avatar textSize={"normal"}>{user.initials}</Avatar>
                <div>
                  <div>{user.name}</div>
                  {user.company && <div className={"text-sm subtle"}>{user.company}</div>}
                  {user.email && <div className={"text-sm subtle"}>{user.email}</div>}
                  {selectedCompany?.accessType &&
                    selectedCompany.accessType !== AccessType.NONE && (
                      <div className={"tw-mt-1"}>
                        <Tag
                          color={accessColors[selectedCompany.accessType]}
                          label={t(accessLabelMappings[selectedCompany.accessType])}
                        />
                      </div>
                    )}
                </div>
              </Box>
              {Array.isArray(userMainAction) ? (
                <>
                  {userMainAction.map(([name, props]) => (
                    <Button key={name} {...props} />
                  ))}
                </>
              ) : (
                userMainAction
              )}
            </VBox>
          ),
          canSelect: false,
        },
        actions.navigateToUserSettings && { type: "separator" },
        actions.navigateToUserSettings && {
          type: "item",
          label: t("settings.user"),
          icon: "gear",
          onSelect: actions.navigateToUserSettings,
        },
        selectedCompany?.accessType === AccessType.ADMIN &&
          actions.openPaymentDialog && {
            type: "item",
            label: t("header.subscriptions"),
            icon: "credit-card",
            onSelect: actions.openPaymentDialog,
          },
        !disableUserContextInMenu && actions.navigateToFeedback && { type: "separator" },
        actions.navigateToFeedback && { type: "label", label: t("help-us-improve") },
        actions.navigateToFeedback && {
          type: "item",
          label: t("give-us-feedback"),
          icon: "chat-text",
          onSelect: actions.navigateToFeedback,
        },
        languages.length && actions.changeLanguage && { type: "separator" },
        languages.length && actions.changeLanguage && { type: "label", label: t("language") },
        ...(actions.changeLanguage ? languages : []).map<DropdownMenuItemType>((l) => ({
          type: "radio-item",
          label: t(`language.${l.language}`),
          isSelected: l.isSelected,
          onSelect: () => {
            actions.changeLanguage?.(l.language);
          },
        })),
        actions.openCookieSettings && { type: "separator" },
        actions.openCookieSettings && {
          type: "item",
          label: t("cookies.cookie-settings"),
          onSelect: actions.openCookieSettings,
        },
        actions.logOut && { type: "separator" },
        actions.logOut && {
          type: "item",
          label: t("header.logout"),
          icon: "sign-out",
          onSelect: actions.logOut,
        },
      ]}
    />
  );
}

function headerClassName(props: { disableDropShadow?: boolean }) {
  return classNames(
    !props.disableDropShadow ? "tw-shadow-md" : "tw-border-b border-gray",
    "tw-z-[1]",
  );
}

export function DesktopAppBar(
  props: AppBarData & {
    disableDropShadow?: boolean;
    subHeaderElement?: ReactElement;
    borderBottom?: boolean;
  },
) {
  const [headerRef, setHeaderRef] = useState<HTMLElement | null>(null);
  const { t } = useTranslation();
  const [showLabel, setShowLabel] = useState(
    () => isTestEnvironment() && !sessionStorage.getItem("hide-test-label"),
  );
  const hideTestLabel = useCallback(() => {
    sessionStorage.setItem("hide-test-label", "1");
    setShowLabel(false);
  }, []);

  const totalSignDocumentCount = getTotalSignDocumentCount(props.signDocumentCounts);
  return (
    <header ref={setHeaderRef} className={headerClassName(props)}>
      {showLabel && <TestEnvironmentMarker onClick={hideTestLabel} />}
      <Box
        paddingX={"md"}
        paddingY={"sm"}
        alignItems={"center"}
        bg
        borderBottom={props.borderBottom !== false && (props.subLinks?.length ?? 0) > 0}
        gap={"md"}
      >
        <Logo
          linkUrl={props.portfolioUrl}
          tooltip={props.backToPortfolioTooltip}
          variant={props.backToPortfolioTooltip ? "back-button" : undefined}
        />
        {props.selectedCompany === undefined && <PortfolioSelector {...props} />}

        <Box grow overflow={"hidden"}>
          <CompanySelector
            {...props}
            buttonClassName={"tw-grow tw-shrink-[1000] tw-min-w-[15ch] tw-max-w-[25ch]"}
          />
          {props.links && props.links.length > 0 && (
            <MenuBar items={props.links} containerRef={headerRef} />
          )}
        </Box>
        <Box marginLeft={"auto"} gap={"lg"} alignItems={"center"}>
          {renderBecomeCustomerButton(props) && (
            <Button
              variant={"primary"}
              label={t("header.become-customer")}
              onClick={props.actions.openPaymentDialog}
              size={"sm"}
            />
          )}
          {renderUpgradeSubscriptionButton(props) && (
            <Button
              label={t("header.upgrade")}
              onClick={props.actions.openPaymentDialog}
              variant={"secondary"}
              size={"sm"}
            />
          )}
          {props.helpCenter && (
            <Box paddingX={"sm"}>
              <HelpCenterLink {...props.helpCenter} />
            </Box>
          )}
          {props.actions.openPortfolioSettings && (
            <PortfolioSettings onClick={props.actions.openPortfolioSettings} />
          )}
          {totalSignDocumentCount > 0 && props.actions.openBatchSigningPage && (
            <DropdownMenu
              button={{
                type: "icon",
                icon: "pen",
                tag: String(totalSignDocumentCount),
              }}
              items={groupBySigningMethod(props.signDocumentCounts).flatMap(
                ({ signingMethod, count }) => {
                  const label = signingMethodLabel(signingMethod)!;
                  return [
                    {
                      type: "label",
                      label: <Box whitespace={"nowrap"}>{t(label)}</Box>,
                    },
                    {
                      type: "item",
                      label: <Box whitespace={"nowrap"}>{t(signDocumentLabel(count))}</Box>,
                      icon: "pen",
                      onSelect: () => props.actions.openBatchSigningPage?.(signingMethod),
                    },
                  ];
                },
              )}
            />
          )}
          {props.displayUserName && (
            <div className="text-right">
              <div className="semibold">{props.user.name}</div>
              {props.user.email && <div className="text-sm">{props.user.email}</div>}
            </div>
          )}
          <UserMenu {...props} />
        </Box>
      </Box>
      {props.subHeaderElement}
      {props.subLinks && (
        <Box display={"block"} bg>
          <Tabs
            tabs={props.subLinks.map((l) => ({
              id: l.url,
              render: (props) => <SubMenuLink {...l} {...props} badgeColor={l.badgeColor} />,
              onSelect: noop,
              isActive: l.isActive,
            }))}
          />
        </Box>
      )}
    </header>
  );
}

function MobileMenu(props: AppBarData) {
  const { t } = useTranslation();
  return (
    <VBox padding gap={"md"}>
      {props.links?.map((l) => (
        <VBox key={l.url}>
          <a
            href={l.url}
            className={classNames(
              "tw-no-underline",
              "color-default",
              "visited:color-default",
              "active:color-default",
              l.isActive && "tw-font-bold",
            )}
          >
            <Box alignItems={"center"} textSize={"lg"}>
              <Icon name={l.icon} color={l.isActive ? "info" : "default"} />
              <span>{t(l.text)}</span>
              {l.badge && <BetaLabel label={l.badge} color={l.badgeColor} />}
            </Box>
          </a>
          {!isEmpty(l.subLinks) && (
            <VBox alignItems={"start"}>
              {l.subLinks?.map((sub) => (
                <a
                  key={sub.url}
                  href={sub.url}
                  className={classNames(
                    "tw-ml-6",
                    "tw-no-underline",
                    sub.isActive
                      ? ["color-default", "visited:color-default", "active:color-default"]
                      : ["subtle", "visited:subtle", "active:subtle"],
                  )}
                >
                  <Box alignItems={"center"}>
                    <span>{t(sub.text)}</span>
                    {sub.badge && <BetaLabel label={sub.badge} color={sub.badgeColor} />}
                    {sub.tag && <Tag label={sub.tag} color={"error"} />}
                  </Box>
                </a>
              ))}
            </VBox>
          )}
        </VBox>
      ))}
    </VBox>
  );
}

export function MobileAppBar(
  props: AppBarData & { disableDropShadow?: boolean; subHeaderElement?: ReactElement },
) {
  const { t } = useTranslation();
  const { refs, floatingStyles } = useFloatingDropdown({ disableOffset: true, maximize: true });
  return (
    <Popover as={Fragment}>
      {({ open }) => (
        <>
          <header ref={refs.setReference} className={headerClassName(props)}>
            <Box className={styles.mobileHeader} gap={false} bg overflow={"hidden"} padding={"sm"}>
              {isEmpty(props.links) ? (
                <Box gap>
                  <Logo linkUrl={props.portfolioUrl} />
                  <Box>
                    {props.selectedCompany === undefined && <PortfolioSelector {...props} />}
                  </Box>
                </Box>
              ) : (
                <Popover.Button as={Fragment}>
                  <IconButton icon={open ? "x" : "list"} tooltip={t("open-menu")} />
                </Popover.Button>
              )}
              <CompanySelector
                {...props}
                maximizeDropdown
                buttonClassName={"tw-mx-auto tw-text-center"}
              />
              <UserMenu {...props} className={"tw-ml-auto"} />
            </Box>
            {props.subHeaderElement}
          </header>
          {open && (
            <FloatingOverlay layer={"popover"}>
              <div
                ref={refs.setFloating}
                style={floatingStyles}
                className={"tw-overflow-auto tw-border-t border-gray-soft bg-default"}
              >
                <MobileMenu {...props} />
              </div>
            </FloatingOverlay>
          )}
        </>
      )}
    </Popover>
  );
}

export function AppBar({
  data,
  disableDropShadow,
  subHeaderElement,
  borderBottom,
}: {
  data: AppBarData | null;
  disableDropShadow?: boolean;
  subHeaderElement?: ReactElement;
  borderBottom?: boolean;
}) {
  if (!data) {
    return null;
  }
  return isMobileView() ? (
    <MobileAppBar
      {...data}
      disableDropShadow={disableDropShadow}
      subHeaderElement={subHeaderElement}
    />
  ) : (
    <DesktopAppBar
      {...data}
      disableDropShadow={disableDropShadow}
      subHeaderElement={subHeaderElement}
      borderBottom={borderBottom}
    />
  );
}

function renderBecomeCustomerButton(props: AppBarData & { disableDropShadow?: boolean }) {
  const hasActiveSubsubscription = props.subscription?.isActive;
  const hasSelectedCompanyAndIsAdmin =
    props.selectedCompany && props.selectedCompany?.accessType === AccessType.ADMIN;
  return (
    !hasActiveSubsubscription && hasSelectedCompanyAndIsAdmin && props.actions.openPaymentDialog
  );
}

function renderUpgradeSubscriptionButton(props: AppBarData & { disableDropShadow?: boolean }) {
  return (
    props.subscription?.subscriptionType === SubscriptionType.BASIC &&
    props.subscription.subscriptionType.valueOf() !== "SHAREHOLDER_REGISTER" &&
    props.selectedCompany &&
    props.selectedCompany?.accessType === AccessType.ADMIN &&
    props.actions.openPaymentDialog
  );
}

function TestEnvironmentMarker({ onClick }: { onClick: () => void }) {
  return (
    <div
      style={{
        position: "absolute",
        width: "100px",
        paddingLeft: "20px",
        height: "60px",
      }}
      onClick={onClick}
    >
      <span
        style={{
          position: "absolute",
          fontSize: "10px",
          background: "#5C6A8FFF",
          color: "#fff",
          padding: "17px 40px",
          marginTop: "-10px",
          marginLeft: "10px",
          marginRight: "10px",
          fontWeight: "bold",
          transform: "translate(-60%,0) rotate(-45deg)",
          transformOrigin: "center",
          opacity: "60%",
        }}
      >
        TEST
      </span>
    </div>
  );
}
